import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaGithub,
  FaHeart,
  FaRocket,
  FaArrowUp,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";

const Footer = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    checkDarkMode();

    // Scroll to top button visibility
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <>
      <footer
        style={{
          background: isDarkMode
            ? "linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)"
            : "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
          color: "white",
          padding: "3rem 2rem 1.5rem",
          marginTop: "auto",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background particles */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.15, 0] }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 10,
              }}
              style={{
                position: "absolute",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: Math.random() * 3 + 1,
                height: Math.random() * 3 + 1,
                background: `linear-gradient(135deg, #8b5cf6, #3b82f6)`,
                borderRadius: "50%",
                filter: "blur(2px)",
              }}
            />
          ))}
        </div>

        {/* Animated gradient overlay */}
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
          }}
        />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "2rem",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* About Section */}
          <motion.div variants={itemVariants}>
            <div style={{ marginBottom: "1rem" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <HiOutlineSparkles size={22} color="white" />
                </div>
                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #fff, #c4b5fd)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    margin: 0,
                  }}
                >
                  VTUGenX
                </h3>
              </div>
            </div>
            <p
              style={{
                color: "#cbd5e1",
                lineHeight: "1.6",
                marginBottom: "1rem",
                fontSize: "0.9rem",
              }}
            >
              Your ultimate destination for VTU study materials, notes, and
              resources. Empowering students to excel in their academics.
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h4
              style={{
                color: "#8b5cf6",
                marginBottom: "1rem",
                fontSize: "1.1rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaRocket size={14} /> Quick Links
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {["Home", "Notes", "About", "Contact"].map((item) => (
                <li key={item} style={{ marginBottom: "0.75rem" }}>
                  <Link
                    to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                    style={{
                      color: "#cbd5e1",
                      textDecoration: "none",
                      transition: "all 0.3s",
                      fontSize: "0.9rem",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.3rem",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = "#8b5cf6";
                      e.target.style.transform = "translateX(5px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = "#cbd5e1";
                      e.target.style.transform = "translateX(0)";
                    }}
                  >
                    → {item}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Resources */}
          <motion.div variants={itemVariants}>
            <h4
              style={{
                color: "#8b5cf6",
                marginBottom: "1rem",
                fontSize: "1.1rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaArrowUp size={14} /> Resources
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li style={{ marginBottom: "0.75rem" }}>
                <Link
                  to="/upload"
                  style={{
                    color: "#cbd5e1",
                    textDecoration: "none",
                    transition: "all 0.3s",
                    fontSize: "0.9rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#8b5cf6";
                    e.target.style.transform = "translateX(5px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "#cbd5e1";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  → Upload Notes
                </Link>
              </li>
              <li style={{ marginBottom: "0.75rem" }}>
                <Link
                  to="/dashboard"
                  style={{
                    color: "#cbd5e1",
                    textDecoration: "none",
                    transition: "all 0.3s",
                    fontSize: "0.9rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#8b5cf6";
                    e.target.style.transform = "translateX(5px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "#cbd5e1";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  → Dashboard
                </Link>
              </li>
              <li style={{ marginBottom: "0.75rem" }}>
                <Link
                  to="/faq"
                  style={{
                    color: "#cbd5e1",
                    textDecoration: "none",
                    transition: "all 0.3s",
                    fontSize: "0.9rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#8b5cf6";
                    e.target.style.transform = "translateX(5px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "#cbd5e1";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  → FAQ
                </Link>
              </li>
              <li style={{ marginBottom: "0.75rem" }}>
                <Link
                  to="/privacy-policy"
                  style={{
                    color: "#cbd5e1",
                    textDecoration: "none",
                    transition: "all 0.3s",
                    fontSize: "0.9rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#8b5cf6";
                    e.target.style.transform = "translateX(5px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "#cbd5e1";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  → Privacy Policy
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Connect With Us */}
          <motion.div variants={itemVariants}>
            <h4
              style={{
                color: "#8b5cf6",
                marginBottom: "1rem",
                fontSize: "1.1rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaEnvelope size={14} /> Connect With Us
            </h4>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                marginBottom: "1.5rem",
                flexWrap: "wrap",
              }}
            >
              {[
                {
                  Icon: FaFacebook,
                  color: "#1877f2",
                  link: "https://facebook.com",
                },
                {
                  Icon: FaTwitter,
                  color: "#1da1f2",
                  link: "https://twitter.com",
                },
                {
                  Icon: FaInstagram,
                  color: "#e4405f",
                  link: "https://instagram.com",
                },
                {
                  Icon: FaLinkedin,
                  color: "#0077b5",
                  link: "https://linkedin.com",
                },
                { Icon: FaGithub, color: "#333", link: "https://github.com" },
              ].map((item, index) => (
                <motion.a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -5, scale: 1.1 }}
                  style={{
                    color: "#cbd5e1",
                    fontSize: "1.3rem",
                    transition: "all 0.3s",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "38px",
                    height: "38px",
                    borderRadius: "12px",
                    background: isDarkMode
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = item.color;
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isDarkMode
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(255,255,255,0.1)";
                    e.currentTarget.style.color = "#cbd5e1";
                  }}
                >
                  <item.Icon />
                </motion.a>
              ))}
            </div>
            <div
              style={{
                marginBottom: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaEnvelope size={14} color="#8b5cf6" />
              <span style={{ color: "#cbd5e1", fontSize: "0.85rem" }}>
                vtugenx@gmail.com
              </span>
            </div>
            <div
              style={{
                marginBottom: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaPhone size={14} color="#8b5cf6" />
              <span style={{ color: "#cbd5e1", fontSize: "0.85rem" }}>
                {/* +91 1234567890 */}
              </span>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <FaMapMarkerAlt size={14} color="#8b5cf6" />
              <span style={{ color: "#cbd5e1", fontSize: "0.85rem" }}>
                Bengaluru, Karnataka, India
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* Copyright - FIXED for visibility */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          style={{
            textAlign: "center",
            marginTop: "3rem",
            paddingTop: "1.5rem",
            borderTop: `1px solid rgba(255, 255, 255, 0.2)`,
            position: "relative",
            zIndex: 1,
          }}
        >
          <p
            style={{
              fontSize: "0.85rem",
              color: "#cbd5e1",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
              fontWeight: "500",
            }}
          >
            &copy; {new Date().getFullYear()} VTUGenX. All rights reserved.
          </p>
          <p
            style={{
              fontSize: "0.75rem",
              marginTop: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.3rem",
              color: "#cbd5e1",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            Made with{" "}
            <FaHeart
              size={12}
              color="#ef4444"
              style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.2))" }}
            />{" "}
            for VTU students
          </p>
        </motion.div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          onClick={scrollToTop}
          whileHover={{ scale: 1.1, y: -5 }}
          whileTap={{ scale: 0.9 }}
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            width: "48px",
            height: "48px",
            borderRadius: "24px",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            color: "white",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
            zIndex: 1000,
            transition: "all 0.3s",
          }}
        >
          <FaArrowUp size={20} />
        </motion.button>
      )}
    </>
  );
};

export default Footer;
